import { getDate } from '@aikoa/common'
import { type User } from '@supabase/supabase-js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSession } from '~/components/user/SessionProvider'

type OtpState = {
  sentAt: number
  key: string
}

export const OTP_VALIDITY_MS = 5 * 60 * 1000
export const OTP_RESEND_COOLDOWN_MS = 60 * 1000

export function useOtpStateManager() {
  const [otpStates, setOtpStates] = useState<Map<string, OtpState>>(new Map())
  const { session } = useSession()

  const hasRecentOtp = useCallback(
    (key: string) => {
      const state = otpStates.get(key)

      if (!state) {
        return false
      }

      const elapsed = Date.now() - state.sentAt
      return elapsed < OTP_VALIDITY_MS
    },
    [otpStates]
  )

  const canResendOtp = useCallback(
    (key: string) => {
      const state = otpStates.get(key)

      if (!state) {
        return true
      }

      const elapsed = Date.now() - state.sentAt
      return elapsed >= OTP_RESEND_COOLDOWN_MS
    },
    [otpStates]
  )

  const markOtpSent = useCallback(
    (key: string) =>
      setOtpStates((previous) => {
        const next = new Map(previous)
        next.set(key, { sentAt: Date.now(), key })
        return next
      }),
    []
  )

  const hydrateOtpSentAt = useCallback((key: string, sentAtTimestampMs: number) => {
    setOtpStates((previous) => {
      const next = new Map(previous)

      const existing = next.get(key)
      const nextSentAt = Math.max(0, sentAtTimestampMs)

      if (!existing) {
        next.set(key, { key, sentAt: nextSentAt })
        return next
      }

      if (nextSentAt > existing.sentAt) {
        next.set(key, { key, sentAt: nextSentAt })
      }

      return next
    })
  }, [])

  useEffect(() => {
    const user = session?.user as User & {
      phone_change_sent_at?: string | null
    }

    if (!user) {
      return
    }

    if (user.new_email && user.email_change_sent_at) {
      const ts = getDate(user.email_change_sent_at)?.getTime()

      if (!!ts && Number.isFinite(ts)) {
        hydrateOtpSentAt(user.new_email, ts)
      }
    }

    if (user.new_phone && user.phone_change_sent_at) {
      const ts = getDate(user.phone_change_sent_at)?.getTime()

      if (!!ts && Number.isFinite(ts)) {
        hydrateOtpSentAt(user.new_phone, ts)
      }
    }
  }, [session?.user, hydrateOtpSentAt])

  const getResendCooldownRemainingMs = useCallback(
    (key: string | null | undefined) => {
      if (!key) {
        return 0
      }

      const state = otpStates.get(key)

      if (!state) {
        return 0
      }

      const elapsed = Date.now() - state.sentAt
      const remaining = OTP_RESEND_COOLDOWN_MS - elapsed
      return remaining > 0 ? remaining : 0
    },
    [otpStates]
  )

  return {
    hasRecentOtp,
    canResendOtp,
    markOtpSent,
    hydrateOtpSentAt,
    getResendCooldownRemainingMs,
  }
}

export function useOtpCooldownSeconds(
  key: string | null | undefined,
  getRemainingMs: (key: string | null | undefined) => number
) {
  const [remainingMs, setRemainingMs] = useState<number>(() => getRemainingMs(key))

  const remainingSeconds = useMemo(() => Math.ceil(remainingMs / 1000), [remainingMs])

  useEffect(() => {
    if (!key) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setRemainingMs(0)
      return
    }

    const updateRemaining = () => {
      const remaining = getRemainingMs(key)
      setRemainingMs(remaining)
      return remaining
    }

    const initialRemaining = updateRemaining()

    if (initialRemaining <= 0) {
      return
    }

    const interval = setInterval(() => {
      const remaining = updateRemaining()

      if (remaining <= 0) {
        clearInterval(interval)
      }
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [key, getRemainingMs])

  return remainingSeconds > 0 ? remainingSeconds : 0
}
